import React from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { Images } from "../../../utils/Images";

// Styled components
const PopupOverlay = styled(Box)(({ theme, isMobile }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  backdropFilter: "blur(4px)",
}));

const PopupContainer = styled(Box)(({ theme, isMobile }) => ({
  width: isMobile ? "90%" : "441px",
  maxWidth: isMobile ? "400px" : "441px",
  height: isMobile ? "auto" : "532px",
  minHeight: isMobile ? "auto" : "532px",
  maxHeight: isMobile ? "90vh" : "532px",
  opacity: 1,
  gap: isMobile ? "12px" : "20px",
  borderRadius: "10px",
  paddingTop: isMobile ? "16px" : "20px",
  paddingRight: isMobile ? "16px" : "20px",
  paddingBottom: isMobile ? "24px" : "40px",
  paddingLeft: isMobile ? "16px" : "20px",
  background: "#FFFFFF",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
  overflowY: isMobile ? "auto" : "visible",
}));

const CloseButton = styled(Box)(({ theme, isMobile }) => ({
  position: "absolute",
  top: isMobile ? "10px" : "12px",
  right: isMobile ? "10px" : "14px",
  width: isMobile ? "24px" : "26px",
  height: isMobile ? "24px" : "26px",
  cursor: "pointer",
  zIndex: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    width: isMobile ? "24px" : "26px",
    height: isMobile ? "24px" : "26px",
  },
}));

const ImageContainer = styled(Box)(({ theme, isMobile }) => ({
  width: "100%",
  height: isMobile ? "180px" : "272px",
  borderRadius: "10px",
  overflow: "hidden",
  marginBottom: isMobile ? "12px" : "20px",
  position: "relative",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "10px",
  },
}));

const ContentContainer = styled(Box)(({ theme, isMobile }) => ({
  width: isMobile ? "100%" : "366px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const Title = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "18px" : "22px",
  lineHeight: isMobile ? "22px" : "21.6px",
  letterSpacing: "0%",
  color: "#1D1D1B",
  marginBottom: isMobile ? "8px" : "10px",
  textAlign: "center",
}));

const Subtitle = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: isMobile ? "14px" : "16px",
  lineHeight: isMobile ? "20px" : "21.6px",
  letterSpacing: "0%",
  textAlign: "center",
  color: "#4A4A49",
  marginBottom: isMobile ? "16px" : "20px",
}));

const DonateButton = styled(Button)(({ theme, isMobile }) => ({
  width: isMobile ? "100%" : "209px",
  maxWidth: isMobile ? "280px" : "209px",
  height: isMobile ? "44px" : "46px",
  borderRadius: "10px",
  paddingTop: isMobile ? "12px" : "16px",
  paddingRight: isMobile ? "20px" : "24px",
  paddingBottom: isMobile ? "12px" : "16px",
  paddingLeft: isMobile ? "20px" : "24px",
  gap: "8px",
  opacity: 1,
  background: "#F04951",
  boxShadow: "0px 4px 0px 0px #141414",
  // fontFamily: "Red Hat Display",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "13px" : "14px",
  lineHeight: isMobile ? "13px" : "14px",
  letterSpacing: "0%",
  textAlign: "center",
  verticalAlign: "middle",
  color: "#FFFFFF",
  marginBottom: isMobile ? "12px" : "16px",
  textTransform: "none",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "#E03A42",
    boxShadow: "0px 6px 0px 0px #141414",
    transform: "translateY(-2px)",
  },
  "&:active": {
    transform: "translateY(0px)",
    boxShadow: "0px 2px 0px 0px #141414",
  },
}));

const CampaignPopup = ({ isOpen, onClose, campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const campaignTitle = campaignData?.title || "Support this campaign";
  const campaignId = campaignData?.id || null;
  const campaignImage =
    campaignData?.image ||
    campaignData?.thumbnail_image ||
    campaignData?.bannerImage ||
    Images.fallBackImageCard;

  const navigateToDonateOnce = (amount) => {
    navigate("/donate-once-page", {
      state: {
        campaignTitle,
        campaignId,
        amount,
      },
    });
  };

  const handleDonateClick = () => {
    navigateToDonateOnce(5000);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <PopupOverlay isMobile={isMobile} onClick={handleOverlayClick}>
      <PopupContainer isMobile={isMobile}>
        <CloseButton isMobile={isMobile} onClick={onClose}>
          <img src={Images.removeMIcon} alt="Close" />
        </CloseButton>

        <ImageContainer isMobile={isMobile}>
          <img src={campaignImage} alt={campaignTitle} />
        </ImageContainer>

        <ContentContainer isMobile={isMobile}>
          <Title isMobile={isMobile}>{campaignTitle}</Title>

          <Subtitle isMobile={isMobile}>
            Even a little support can create a lifetime of change.
          </Subtitle>

          <DonateButton
            onClick={handleDonateClick}
            isMobile={isMobile}
          >
            Donate Now ₹5000
          </DonateButton>
        </ContentContainer>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default CampaignPopup;
